/**
 * useMortgageMonitor
 *
 * Data + actions for the Mortgage Monitor page (refi radar on the funded
 * Arive book). Wraps four callables:
 *   getMortgageMonitor    — opportunities + stats + rate trend + settings
 *   actOnRefiOpportunity  — dismiss / reopen / mark_contacted / create_task / enroll
 *   refreshMarketRates    — admin: pull FRED rates now
 *   setManualMarketRates  — admin: hand-type today's rates (hold until next fetch)
 *   runRefiScanNow        — admin: re-score the book now
 *   backfillFundedLoanDetails — admin: discovery + detail enrichment passes
 *
 * All callables live in us-east1.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { app } from '../utils/init-firebase';

const functions = getFunctions(app, 'us-east1');

export function useMortgageMonitor() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [ownerFilter, setOwnerFilter] = useState('all'); // admins only; LOs are server-locked to self
  const [actioningId, setActioningId] = useState(null);
  const [adminBusy, setAdminBusy] = useState(null); // 'rates' | 'scan' | 'backfill' | null

  // Agent tools poll this ref via waitForIdle so results report fresh data.
  const loadingRef = useRef(true);
  useEffect(() => { loadingRef.current = loading; }, [loading]);

  const load = useCallback(async (owner = ownerFilter) => {
    setLoading(true);
    setError(null);
    try {
      const call = httpsCallable(functions, 'getMortgageMonitor');
      const res = await call({ ownerUid: owner || 'all' });
      setData(res.data);
    } catch (err) {
      console.error('getMortgageMonitor failed:', err);
      setError(err.message || 'Failed to load the Mortgage Monitor');
    } finally {
      setLoading(false);
    }
  }, [ownerFilter]);

  useEffect(() => { load(ownerFilter); }, [load, ownerFilter]);

  /** dismiss | reopen | mark_contacted | create_task | enroll */
  const act = useCallback(async (opportunityId, action, extra = {}) => {
    setActioningId(opportunityId);
    try {
      const call = httpsCallable(functions, 'actOnRefiOpportunity');
      const res = await call({ opportunityId, action, ...extra });
      await load();
      return { success: true, ...res.data };
    } catch (err) {
      console.error('actOnRefiOpportunity failed:', err);
      return { success: false, error: err.message || 'Action failed' };
    } finally {
      setActioningId(null);
    }
  }, [load]);

  const refreshRates = useCallback(async () => {
    setAdminBusy('rates');
    try {
      const call = httpsCallable(functions, 'refreshMarketRates');
      const res = await call({});
      await load();
      return { success: true, ...res.data };
    } catch (err) {
      return { success: false, error: err.message || 'Rate refresh failed' };
    } finally {
      setAdminBusy(null);
    }
  }, [load]);

  /** rates: { '30yr_fixed': 6.25, ... } — only the products the admin changed. */
  const setManualRates = useCallback(async (rates) => {
    setAdminBusy('manualRates');
    try {
      const call = httpsCallable(functions, 'setManualMarketRates');
      const res = await call({ rates });
      await load();
      return { success: true, ...res.data };
    } catch (err) {
      return { success: false, error: err.message || 'Manual rate save failed' };
    } finally {
      setAdminBusy(null);
    }
  }, [load]);

  const runScan = useCallback(async () => {
    setAdminBusy('scan');
    try {
      const call = httpsCallable(functions, 'runRefiScanNow');
      const res = await call({});
      await load();
      return { success: true, ...res.data };
    } catch (err) {
      return { success: false, error: err.message || 'Scan failed' };
    } finally {
      setAdminBusy(null);
    }
  }, [load]);

  const runBackfill = useCallback(async ({ dryRun = true, limit = 150 } = {}) => {
    setAdminBusy('backfill');
    try {
      const call = httpsCallable(functions, 'backfillFundedLoanDetails');
      const res = await call({ dryRun, limit });
      if (!dryRun) await load();
      return { success: true, ...res.data };
    } catch (err) {
      return { success: false, error: err.message || 'Backfill failed' };
    } finally {
      setAdminBusy(null);
    }
  }, [load]);

  return {
    data,
    loading,
    loadingRef,
    error,
    ownerFilter,
    setOwnerFilter,
    refresh: load,
    act,
    actioningId,
    adminBusy,
    refreshRates,
    setManualRates,
    runScan,
    runBackfill,
  };
}

export default useMortgageMonitor;
